// Filename deviates from the BrandKit* sibling convention per the PLAT-9516
// Jira description — this section edits Showcase-level fields, not Branding
// fields. See docs/plans/PLAT-9511-translations-foundation/PLAT-9516-*.md.
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Accordion from './Accordion';

import type { Language } from '@src/types.ts/language';
import type { KeyboardEvent } from 'react';

const logger = LogManager.getLogger('brand-kit:ShowcaseLanguageSettings');

const DEFAULT_LANGUAGE_LABEL = 'American English (en-US)';

const ShowcaseLanguageSettings = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore, languageStore } = useStore();
  const organization = videoCreator.organization;

  const currentTranslationsEnabled = organization?.translationsEnabled ?? false;
  const currentLanguages = useMemo(
    () => organization?.supportedLanguages ?? [],
    [organization?.supportedLanguages],
  );
  const currentDefaultLanguage = organization?.defaultLanguage ?? '';

  const [translationsEnabled, setTranslationsEnabled] = useState<boolean>(
    currentTranslationsEnabled,
  );
  const [selected, setSelected] = useState<Language[]>(currentLanguages);
  const [defaultLanguage, setDefaultLanguage] = useState<string>(
    currentDefaultLanguage,
  );
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    languageStore.loadAll();
  }, [languageStore]);

  useEffect(() => {
    setTranslationsEnabled(currentTranslationsEnabled);
    setSelected(currentLanguages);
    setDefaultLanguage(currentDefaultLanguage);
    setQuery('');
    setIsSaved(false);
  }, [
    organization?.id,
    currentTranslationsEnabled,
    currentLanguages,
    currentDefaultLanguage,
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const selectedIds = useMemo(
    () => new Set(selected.map(l => l.id)),
    [selected],
  );

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const pool = languageStore.activeLanguages.filter(
      l => !selectedIds.has(l.id),
    );
    if (!trimmed) return pool;
    return pool.filter(l => {
      const caption = l.caption.toLowerCase();
      const value = l.value.toLowerCase();
      const base = (l.baseLanguage || '').toLowerCase();
      return (
        caption.includes(trimmed) ||
        value.includes(trimmed) ||
        base.includes(trimmed)
      );
    });
  }, [languageStore.activeLanguages, selectedIds, query]);

  // Reset highlight to the top whenever the filtered list shifts.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, filteredOptions.length]);

  // Keep the highlighted option visible inside the dropdown.
  useEffect(() => {
    if (!isOpen) return;
    const el = dropdownRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  // The default (source) language stored as a BCP-47 value (e.g. "en-US").
  // Canonicalize the current value against the active-language list so the
  // select matches regardless of stored casing ("en-us" ↔ "en-US").
  const resolvedDefaultLanguage = useMemo(() => {
    if (!defaultLanguage) return '';
    const match = languageStore.activeLanguages.find(
      l => l.value.toLowerCase() === defaultLanguage.toLowerCase(),
    );
    return match?.value ?? defaultLanguage;
  }, [languageStore.activeLanguages, defaultLanguage]);

  const isDirty = useMemo(() => {
    if (translationsEnabled !== currentTranslationsEnabled) return true;
    if (
      defaultLanguage.trim().toLowerCase() !==
      currentDefaultLanguage.trim().toLowerCase()
    )
      return true;
    const currentIds = currentLanguages
      .map(l => l.id)
      .sort()
      .join(',');
    const selectedIds = selected
      .map(l => l.id)
      .sort()
      .join(',');
    return currentIds !== selectedIds;
  }, [
    translationsEnabled,
    currentTranslationsEnabled,
    defaultLanguage,
    currentDefaultLanguage,
    currentLanguages,
    selected,
  ]);

  const handleAdd = (lang: Language) => {
    setSelected(prev => [...prev, lang]);
    setQuery('');
    setIsSaved(false);
    inputRef.current?.focus();
  };

  const handleRemove = (id: string) => {
    setSelected(prev => prev.filter(l => l.id !== id));
    setIsSaved(false);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query.length === 0 && selected.length > 0) {
      e.preventDefault();
      const last = selected[selected.length - 1];
      handleRemove(last.id);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      if (filteredOptions.length === 0) return;
      setActiveIndex(i => Math.min(i + 1, filteredOptions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      if (filteredOptions.length === 0) return;
      setActiveIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Home') {
      if (filteredOptions.length === 0) return;
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === 'End') {
      if (filteredOptions.length === 0) return;
      e.preventDefault();
      setActiveIndex(filteredOptions.length - 1);
      return;
    }
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      const idx = Math.min(
        Math.max(activeIndex, 0),
        filteredOptions.length - 1,
      );
      handleAdd(filteredOptions[idx]);
      return;
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;
    const previousTranslationsEnabled = organization.translationsEnabled;
    const previousLanguages = organization.supportedLanguages;
    const previousDefaultLanguage = organization.defaultLanguage;
    setIsSaving(true);
    setIsSaved(false);
    try {
      organization.translationsEnabled = translationsEnabled;
      organization.supportedLanguages = selected;
      organization.defaultLanguage = resolvedDefaultLanguage || undefined;
      await datoClientStore.albumRepository?.update(organization);
      setIsSaved(true);
    } catch (err) {
      organization.translationsEnabled = previousTranslationsEnabled;
      organization.supportedLanguages = previousLanguages;
      organization.defaultLanguage = previousDefaultLanguage;
      setTranslationsEnabled(previousTranslationsEnabled ?? false);
      setSelected(previousLanguages ?? []);
      setDefaultLanguage(previousDefaultLanguage ?? '');
      const info = extractErrorInfo(err);
      logger.error('Failed to save showcase language settings', info);
      const detail = info?.errorMessage || 'Please try again.';
      videoCreator.showToast({
        state: 'error',
        message: `Could not save language settings. ${detail}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = isSaving || !isDirty;
  const comboboxDisabled =
    isSaving || languageStore.loadingStatus === 'loading';

  return (
    <Accordion
      id="languages"
      title="Languages"
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <Container isSaving={isSaving}>
        <DefaultLanguageField>
          <label htmlFor="default-language-select">Default Language</label>
          <div className="hint">
            The source language stories in this showcase are written in.
          </div>
          <select
            id="default-language-select"
            value={resolvedDefaultLanguage}
            disabled={comboboxDisabled}
            onChange={e => {
              setDefaultLanguage(e.target.value);
              setIsSaved(false);
            }}
          >
            <option value="">Select a default language…</option>
            {resolvedDefaultLanguage &&
              !languageStore.activeLanguages.some(
                l => l.value === resolvedDefaultLanguage,
              ) && (
                <option value={resolvedDefaultLanguage}>
                  {resolvedDefaultLanguage}
                </option>
              )}
            {languageStore.activeLanguages.map(l => (
              <option key={l.id} value={l.value}>
                {l.caption} ({l.value})
              </option>
            ))}
          </select>
        </DefaultLanguageField>

        <ToggleRow>
          <div className="text">
            <div className="title">Enable additional languages</div>
            <div className="hint">
              {translationsEnabled
                ? 'Stories in this showcase can be translated into any of the supported languages you select below.'
                : `Only ${DEFAULT_LANGUAGE_LABEL} is available. Turn on to translate stories into additional languages.`}
            </div>
          </div>
          <Switch
            role="switch"
            aria-checked={translationsEnabled}
            aria-label="Enable additional languages"
            data-on={translationsEnabled}
            disabled={isSaving}
            onClick={() => {
              if (isSaving) return;
              setTranslationsEnabled(v => !v);
              setIsSaved(false);
            }}
            type="button"
          >
            <span className="thumb" />
          </Switch>
        </ToggleRow>

        {translationsEnabled && (
          <>
            <Description>
              Restrict this showcase&apos;s translation flows to a specific
              subset of languages. Leave empty to allow{' '}
              <strong>every active language</strong>.
            </Description>

            <Combobox
              ref={comboboxRef}
              onClick={() => {
                if (!comboboxDisabled) {
                  setIsOpen(true);
                  inputRef.current?.focus();
                }
              }}
              isOpen={isOpen}
              isDisabled={comboboxDisabled}
            >
              <div className="field">
                {selected.map(lang => (
                  <Chip key={lang.id}>
                    <span className="caption">{lang.caption}</span>
                    <span className="code">{lang.value}</span>
                    <button
                      type="button"
                      className="remove"
                      onClick={e => {
                        e.stopPropagation();
                        handleRemove(lang.id);
                      }}
                      aria-label={`Remove ${lang.caption}`}
                      disabled={isSaving}
                    >
                      ×
                    </button>
                  </Chip>
                ))}
                <input
                  ref={inputRef}
                  value={query}
                  placeholder={
                    selected.length === 0
                      ? 'Type to search languages (e.g. Spanish, fr-CA)…'
                      : 'Add another…'
                  }
                  onChange={e => {
                    setQuery(e.target.value);
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={handleInputKeyDown}
                  disabled={comboboxDisabled}
                />
              </div>
              {isOpen && (
                <Dropdown ref={dropdownRef} role="listbox">
                  {languageStore.loadingStatus === 'loading' ? (
                    <EmptyState>Loading languages…</EmptyState>
                  ) : filteredOptions.length === 0 ? (
                    <EmptyState>
                      {languageStore.activeLanguages.length === 0
                        ? 'No active languages are configured.'
                        : query.trim()
                          ? `No languages match "${query}".`
                          : 'All active languages are already selected.'}
                    </EmptyState>
                  ) : (
                    filteredOptions.map((lang, index) => (
                      <button
                        key={lang.id}
                        type="button"
                        role="option"
                        aria-selected={index === activeIndex}
                        data-option-index={index}
                        data-active={index === activeIndex}
                        className="option"
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => {
                          e.stopPropagation();
                          handleAdd(lang);
                        }}
                      >
                        <span className="caption">{lang.caption}</span>
                        <span className="code">{lang.value}</span>
                      </button>
                    ))
                  )}
                </Dropdown>
              )}
            </Combobox>
          </>
        )}

        <div className="actions">
          {translationsEnabled && selected.length > 0 && (
            <button
              type="button"
              className="clear-btn"
              onClick={() => {
                setSelected([]);
                setIsSaved(false);
              }}
              disabled={isSaving}
            >
              Clear
            </button>
          )}
          <button
            className="save-btn"
            disabled={saveDisabled}
            onClick={handleSave}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Container>
    </Accordion>
  );
});

const Container = styled.div<{ isSaving?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .actions {
    display: flex;
    align-self: flex-end;
    gap: 24px;

    > button {
      display: block;
      height: 48px;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      border: none;
      transition: var(--transition-normal);
      opacity: ${({ isSaving }) => (isSaving ? 0.6 : 1)};
      cursor: ${({ isSaving }) => (isSaving ? 'not-allowed' : 'pointer')};
      &:hover:not(:disabled) {
        filter: brightness(1.2) saturate(1.2);
      }
      &.clear-btn {
        background: transparent;
        border: 1px solid var(--border);
        color: var(--text-muted);
        &:hover:not(:disabled) {
          filter: none;
          border-color: var(--destructive);
          color: var(--destructive);
        }
      }
      &.save-btn {
        background: var(--primary);
        color: var(--background);
      }
      &:disabled {
        color: var(--text-muted);
        border: 1px solid var(--text-muted);
        background: transparent;
        cursor: not-allowed;
      }
    }
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    .title {
      color: var(--foreground);
      font-size: var(--text-base);
      font-weight: var(--font-weight-semibold);
    }
    .hint {
      color: var(--text-muted);
      font-size: var(--text-sm);
      line-height: 1.5;
    }
  }
`;

const Switch = styled.button<{ 'data-on': boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: ${({ 'data-on': on }) =>
    on ? 'var(--primary)' : 'var(--surface-elevated)'};
  cursor: pointer;
  padding: 0;
  transition: var(--transition-normal);

  .thumb {
    position: absolute;
    top: 50%;
    left: ${({ 'data-on': on }) => (on ? 'calc(100% - 22px)' : '2px')};
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ 'data-on': on }) =>
      on ? 'var(--background)' : 'var(--text-muted)'};
    transition: var(--transition-normal);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DefaultLanguageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  > label {
    color: var(--foreground);
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
  }

  > .hint {
    color: var(--text-muted);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  > select {
    margin-top: 2px;
    height: 48px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: transparent;
    color: var(--foreground);
    font-size: var(--text-sm);
    font-family: var(--font-primary);
    cursor: pointer;
    transition: var(--transition-normal);

    &:focus {
      outline: none;
      border-color: var(--primary);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    > option {
      background: var(--surface);
      color: var(--foreground);
    }
  }
`;

const Description = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin: 0;

  > strong {
    color: var(--foreground);
    font-weight: var(--font-weight-semibold);
  }
`;

const Combobox = styled.div<{ isOpen: boolean; isDisabled: boolean }>`
  position: relative;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'text')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};

  .field {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-height: 48px;
    padding: 6px 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: transparent;
    transition: var(--transition-normal);

    &:focus-within {
      border-color: var(--primary);
    }

    > input {
      flex: 1;
      min-width: 160px;
      border: none;
      outline: none;
      background: transparent;
      padding: 6px 4px;
      color: var(--foreground);
      font-size: var(--text-sm);
      font-family: var(--font-primary);

      &::placeholder {
        color: var(--text-muted);
      }
      &:disabled {
        cursor: not-allowed;
      }
    }
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px 2px 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  color: var(--foreground);
  font-size: var(--text-sm);

  .caption {
    font-weight: var(--font-weight-medium);
  }

  .code {
    color: var(--text-muted);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-muted);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    border-radius: 999px;
    transition: var(--transition-normal);

    &:hover:not(:disabled) {
      background-color: var(--surface-elevated);
      color: var(--destructive);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100005;
  max-height: 320px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-md);
  padding: 4px 0;

  .option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 10px 16px;
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    color: var(--foreground);
    transition: var(--transition-normal);

    &[data-active='true'] {
      background: var(--primary);
      color: var(--background);

      .code {
        color: var(--background);
        opacity: 0.85;
      }
    }

    .caption {
      font-size: var(--text-sm);
    }

    .code {
      color: var(--text-muted);
      font-size: var(--text-xs);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`;

const EmptyState = styled.div`
  padding: 12px 16px;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

export default ShowcaseLanguageSettings;
